import { graphlib, layout } from "@dagrejs/dagre";
import { useEffect } from "react";
import {
  Position,
  useReactFlow,
  useStore
} from "reactflow";
import { CANVAS_DIRECTIONS } from "constants/flowDesigner";
import { NODE_TYPES, EDGE_TYPES } from "constants/flowDesigner";

// // the layout direction (T = top, R = right, B = bottom, L = left, TB = top to bottom, ...)
// export type Direction = 'TB' | 'LR' | 'RL' | 'BT';

// export type Options = {
//   direction: Direction;
// };

const positionMap = {
  T: Position.Top,
  L: Position.Left,
  R: Position.Right,
  B: Position.Bottom
};

const nodeCountSelector = (state) => state.nodeInternals.size;
const nodesInitializedSelector = (state) =>
  Array.from(state.nodeInternals.values()).every((node) => node.width && node.height);

function useAutoLayout({ direction = CANVAS_DIRECTIONS.VERTICAL, manualRun = false }) {
  const nodeCount = useStore(nodeCountSelector);
  const nodesInitialized = useStore(nodesInitializedSelector);
  const { getNodes, getEdges, setNodes, setEdges, fitView } = useReactFlow();
  const isHorizontalLayout = direction === CANVAS_DIRECTIONS.HORIZONTAL;
  const nodesep = isHorizontalLayout ? 100 : 300;
  const ranksep = isHorizontalLayout ? 150 : 50;

  const updateLayout = () => {
    // only run the layout if there are nodes and they have been initialized with their dimensions
    if (!nodeCount || !nodesInitialized) {
      return;
    }

    const dagreGraph = new graphlib.Graph();

    dagreGraph.setDefaultEdgeLabel(() => ({}));

    let nodes = getNodes().filter((n) => n.type !== NODE_TYPES.SUBFLOW);
    let edges = getEdges().filter((e) => e.type !== EDGE_TYPES.STRAIGHT);

    dagreGraph.setGraph({ rankdir: direction, nodesep, ranksep });

    nodes.forEach((node) => {
      dagreGraph.setNode(node.id, {
        width: node.width ?? 0,
        height: node.height ?? 0
      });
    });

    edges.forEach((edge) => {
      if (edge.type !== EDGE_TYPES.FLOATING)
        dagreGraph.setEdge(edge.source, edge.target, { height: 50, width: 50 });
    });

    layout(dagreGraph);

    nodes.forEach((node) => {
      if (node.type === NODE_TYPES.SUBFLOW) {
        const invokeNode = dagreGraph.node(node.data?.parentNodeId);
        if (invokeNode) {
          node.x = invokeNode.x - invokeNode.width / 2 - node.width / 2 - 60;
          node.y = invokeNode.y;
        }
      }
    });

    // Group label nodes
    const labelGroups = {};
    nodes.forEach((node) => {
      if (node.type === NODE_TYPES.LABEL && node.data?.source) {
        const groupKey = node.data.source;
        if (!labelGroups[groupKey]) labelGroups[groupKey] = [];
        labelGroups[groupKey].push(node.id);
      }
    });

    // Get the min X or Y for each group to align labels
    const labelAlignMap = {};
    Object.entries(labelGroups).forEach(([, ids]) => {
      if (ids.length > 1) {
        if (isHorizontalLayout) {
          const minX = Math.min(...ids.map((id) => dagreGraph.node(id).x));
          ids.forEach((id) => {
            labelAlignMap[id] = { x: minX };
          });
        } else {
          const minY = Math.min(...ids.map((id) => dagreGraph.node(id).y));
          ids.forEach((id) => {
            labelAlignMap[id] = { y: minY };
          });
        }
      }
    });

    const getSubFlowPosition = (node) => {
      if (isHorizontalLayout) {
        return {
          x: node.x,
          y: node.y + node.height / 2 + node.height / 2 + 60
        };
      }
      return {
        x: node.x - node.width / 2 - node.width / 2 - 60,
        y: node.y
      };
    };

    setNodes((nodes) =>
      nodes.map((node) => {
        let x, y;
        if (node.type === NODE_TYPES.SUBFLOW) {
          x = node.x ?? 0;
          y = node.y ?? 0;
        } else {
          let dagre = dagreGraph.node(node.id);
          x = dagre.x;
          y = dagre.y;
        }

        // Adjust x/y for label nodes in a group
        if (labelAlignMap[node.id]) {
          if (isHorizontalLayout && labelAlignMap[node.id].x !== undefined) {
            x = labelAlignMap[node.id].x;
          } else if (!isHorizontalLayout && labelAlignMap[node.id].y !== undefined) {
            y = labelAlignMap[node.id].y;
          }
        }

        if (node.type === NODE_TYPES.SUBFLOW) {
          const invokeNode = dagreGraph.node(node.data?.parentNodeId);
          if (invokeNode) {
            x = getSubFlowPosition(invokeNode).x;
            y = getSubFlowPosition(invokeNode).y;
          }
        }

        return {
          ...node,
          sourcePosition: positionMap[direction[1]],
          targetPosition: positionMap[direction[0]],
          position: {
            x: x - node.width / 2,
            y: y - node.height / 2
          },
          style: { ...node.style, opacity: 1 }
        };
      })
    );

    setEdges((edges) => {
      return edges.map((edge) => ({ ...edge, style: { ...edge.style, opacity: 1 } }));
    });
  };

  useEffect(() => {
    if (manualRun) return;

    updateLayout();
  }, [nodeCount, nodesInitialized, getNodes, getEdges, setNodes, setEdges, fitView, direction]);

  return {
    updateLayout
  };
}

export default useAutoLayout;
